import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ArrowLeft } from '../../../Assets/images/logos';
import {
  MainWrapper,
  TitleStyles,
  GapContainer,
  BottomContainer,
  Wrapper,
  StyledButton
} from './FarmDetailsForm.style';
import { Checkbox, Field, Input, LinkButton, Toast } from '@single-platform/components';
import {
  ClientConsentContainer,
  ClientConsentText
} from '../Modals/CompanyModal/CompanyDisplayContainer/CompanyRepresentativeForm/CompanyRepresentativeForm.style';
import * as Yup from 'yup';
import { useFormValidation } from '../../ApplicationKYC/LegalEntityQuestionnaire/validation/useFormValidation';
import { useLegalLinks } from '../../../Hooks/useLegalLinks';
import { steps } from '../Modals/CompanyModal/CompanyDisplayContainer/CompanyDisplayContainer';

const INVALID_START_PATTERN = /^[3456]/;

const isInvalidCode = (code) => {
  if (code && code.length >= 11) {
    return INVALID_START_PATTERN.test(code);
  }
  return false;
};

export const FarmDetailsForm = ({ handleStepChange, setData }) => {
  const { t } = useTranslation();
  const isBank = process.env.REACT_APP_TRADEMARK.includes('Bank');
  const [showConfirmCodeConsent, setShowConfirmCodeConsent] = React.useState(false);

  const { openTermsAndConditions, openPrivacyPolicy } = useLegalLinks(isBank);

  const validationSchema = Yup.object({
    country: Yup.string().required(t('validation_required_field')),
    code: Yup.string()
      .test('is-valid-farm-code', t('validation_required_field'), (value) => value.length > 4)
      .required(t('validation_required_field')),
    TermsAndConditionsConsent: Yup.string()
      .test('match-true', 'validation_required_field', (value) => value === 'true')
      .required(t('validation_required_field')),
    MarketingDataUseConsent: Yup.string().optional(),
    MarketingNewsletterConsent: Yup.string().optional(),
    ConfirmFarmCode: Yup.string().when('code', {
      is: (code) => {
        return isInvalidCode(code);
      },
      then: Yup.string()
        .test('match-true', ' ', (value) => value === 'true')
        .required(t('validation_required_field')),
      otherwise: Yup.string().optional()
    })
  });

  const initialValues = {
    country: 'LT',
    code: '',
    TermsAndConditionsConsent: false,
    MarketingDataUseConsent: false,
    MarketingNewsletterConsent: false,
    ConfirmFarmCode: false
  };

  const { handleSubmit, setTouchedOnAll, validationHelpers, getValues } = useFormValidation({
    initialValues,
    validationSchema
  });

  const handleCodeChange = (value) => {
    validationHelpers.setValue('code', value);
    setShowConfirmCodeConsent(value.length >= 11 && INVALID_START_PATTERN.test(value));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(
      (data) => {
        setData(data);
        handleStepChange(steps.who_representing_farm);
      },
      // on invalid case
      () => setTouchedOnAll()
    )();
  };

  return (
    <MainWrapper>
      <Wrapper>
        <ArrowLeft onClick={() => handleStepChange(steps.type_of_account)} />
        <TitleStyles>{t('farm_details_title')}</TitleStyles>
        <GapContainer>
          {showConfirmCodeConsent && (
            <Toast
              isVisible
              variant="info"
              header={t('attention')}
              message={t('farm_code_attention_message')}
            />
          )}
          <Field
            Component={Input}
            name="code"
            type="number"
            placeholder={t('farm_code')}
            validationHelpers={validationHelpers}
            value={getValues('code')}
            onChange={handleCodeChange}
          />
        </GapContainer>
        {showConfirmCodeConsent && (
          <ClientConsentContainer>
            <Field
              Component={Checkbox}
              name="ConfirmFarmCode"
              id="ConfirmFarmCode"
              validationHelpers={validationHelpers}
              value={getValues('ConfirmFarmCode')}
              validateOnChange
            />
            <ClientConsentText>{t('confirm_farm_code_consent')}</ClientConsentText>
          </ClientConsentContainer>
        )}
      </Wrapper>
      <BottomContainer>
        <ClientConsentContainer>
          <Field
            Component={Checkbox}
            name="MarketingNewsletterConsent"
            id="MarketingNewsletterConsent"
            validationHelpers={validationHelpers}
            value={getValues('MarketingNewsletterConsent')}
            validateOnChange
          />
          <ClientConsentText>{t('marketing_news_letter_consent')}</ClientConsentText>
        </ClientConsentContainer>

        <ClientConsentContainer>
          <Field
            Component={Checkbox}
            name="MarketingDataUseConsent"
            id="MarketingDataUseConsent"
            validationHelpers={validationHelpers}
            value={getValues('MarketingDataUseConsent')}
            validateOnChange
          />
          <ClientConsentText>{t('marketing_data_use_consent')}</ClientConsentText>
        </ClientConsentContainer>

        <ClientConsentContainer>
          <Field
            Component={Checkbox}
            name="TermsAndConditionsConsent"
            id="TermsAndConditionsConsent"
            validationHelpers={validationHelpers}
            value={getValues('TermsAndConditionsConsent')}
            validateOnChange
          />
          <ClientConsentText>
            <Trans i18nKey="client_consent_text">
              Text
              <LinkButton onClick={openTermsAndConditions}>Link</LinkButton>
              Text
              <LinkButton onClick={openPrivacyPolicy}>Link</LinkButton>
            </Trans>
          </ClientConsentText>
        </ClientConsentContainer>
        <Field Component={StyledButton} type="submit" onClick={onSubmit}>
          {t('button_save_and_continue')}
        </Field>
      </BottomContainer>
    </MainWrapper>
  );
};
